import React from 'react';
import { useScan } from '../../contexts/ScanContext';
import { useNavigate } from 'react-router-dom';
import ConnectGitHubCard from '../ConnectGitHubCard';

const getStatusChip = (repo) => {
  // Returns a dot and label, left-aligned, matching the screenshot
  if (repo.total_vulnerabilities > 0) {
    if (
      repo.latest_scan_status &&
      repo.latest_scan_status.toLowerCase().includes('critical')
    ) {
      return (
        <div className="flex items-center gap-2">
          <span className="w-3 h-3 rounded-full bg-red-500 inline-block"></span>
          <span className="text-sm text-gray-700">Critical vulnerabilities found</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-yellow-500 inline-block"></span>
        <span className="text-sm text-gray-700">High risk vulnerabilities detected</span>
      </div>
    );
  }
  if (repo.total_vulnerabilities === 0 && repo.total_scans > 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-green-500 inline-block"></span>
        <span className="text-sm text-gray-700">No vulnerabilities found</span>
      </div>
    );
  }
  if (repo.total_scans === 0) {
    return (
      <div className="flex items-center gap-2">
        <span className="w-3 h-3 rounded-full bg-gray-400 inline-block"></span>
        <span className="text-sm font-semibold text-gray-500">Not scanned yet</span>
      </div>
    );
  }
  return null;
};

const RepositoryList = ({ lastUpdated, repoCount }) => {
  const {
    repositories,
    loadingRepos,
    error,
    loadRepositories,
    selectRepository,
  } = useScan();

  const navigate = useNavigate();

  React.useEffect(() => {
    loadRepositories();
  }, [loadRepositories]);

  const handleCardClick = (repo) => {
    selectRepository(repo.repo_url);
    const repoPath = new URL(repo.repo_url).pathname;
    navigate(`/repositories${repoPath}`);
  };

  // Sort by latest_scan_time (descending)
  const sortedRepos = [...repositories].sort((a, b) => {
    if (!a.latest_scan_time && !b.latest_scan_time) return 0;
    if (!a.latest_scan_time) return 1;
    if (!b.latest_scan_time) return -1;
    return new Date(b.latest_scan_time) - new Date(a.latest_scan_time);
  });

  if (loadingRepos) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-red-600 text-center p-4">
        {error}
      </div>
    );
  }

  return (
    <div>
      {/* Last updated and count */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="text-gray-900 text-base">
          <span className="font-semibold">Your Repositories</span>
          {lastUpdated && (
            <span className="ml-2 text-xs text-gray-500">Last updated: {lastUpdated}</span>
          )}
        </div>
        <div className="text-gray-600 text-sm">{repoCount} repositories</div>
      </div>
      {/* Grid of cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-4 sm:gap-8">
        {sortedRepos.map((repo) => (
          <div
            key={repo.repo_url}
            onClick={() => handleCardClick(repo)}
            className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 flex flex-col min-h-[200px] w-full max-w-full cursor-pointer hover:border-teal-400 transition-transform duration-200 ease-in-out hover:scale-105 hover:shadow-lg"
          >
            {/* Repo name and link */}
            <div className="mb-2">
              <div className="font-bold text-gray-900 text-lg leading-tight truncate flex items-center gap-2">
                <span role="img" aria-label="repo" className="text-xl">📁</span>
                {repo.repo_url.split('/').pop()}
              </div>
              <a
                href={repo.repo_url}
                target="_blank"
                rel="noopener noreferrer"
                className="text-xs text-gray-500 hover:underline truncate block mt-1"
                onClick={(e) => e.stopPropagation()}
              >
                {repo.repo_url}
              </a>
            </div>
            {/* Stats row */}
            <div className="flex items-end gap-6 sm:gap-8 mt-4 mb-2">
              <div className="flex flex-col items-start">
                <span className="text-2xl text-gray-900 leading-none">{repo.total_scans}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wide mt-1">Scans</span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-2xl text-gray-900 leading-none">{repo.total_vulnerabilities}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wide mt-1">Vulnerabilities</span>
              </div>
              <div className="flex flex-col items-start">
                <span className="text-2xl text-gray-900 leading-none">{repo.security_score ?? '-'}</span>
                <span className="text-xs text-gray-500 uppercase tracking-wide mt-1">Score</span>
              </div>
            </div>
            {/* Status chip below stats row */}
            <div className="mt-2 mb-2">
              {getStatusChip(repo)}
            </div>
            {/* Horizontal line */}
            <div className="border-t border-gray-200 my-2"></div>
            {/* Date at bottom left */}
            <div className="mt-auto pt-1 text-xs text-gray-500">
              {repo.latest_scan_time ? new Date(repo.latest_scan_time).toLocaleDateString('en-GB') : '-'}
            </div>
          </div>
        ))}
        {repositories.length === 0 && (
          <div className="col-span-full py-4">
            <ConnectGitHubCard />
          </div>
        )}
      </div>
    </div>
  );
};

export default RepositoryList; 